import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ProjectHelper } from '../project.helper';
import {
    CostManagerDto,
    PublicGanttChartDto,
    GanttClientTaskDto,
    ProjectDto,
    WorkTaskListDto,
} from 'app/api/models';
import {
    AccountService,
    CostManagerService,
    ProjectService,
    ResourceService,
    WorkloadService,
} from 'app/api/services';
import { EventSettingsModel } from '@syncfusion/ej2-angular-schedule';
import {
    EditSettingsModel,
    GanttAllModule,
    GanttComponent,
    PdfExportProperties,
} from '@syncfusion/ej2-angular-gantt';
import { TimeUtil } from 'app/core/utils/time-utils';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { MatDialog } from '@angular/material/dialog';
import { JwtAuthService } from 'app/core/services/cookie-jwt-auth/jwt-auth.service';
import { DatePipe } from '@angular/common';
import { PdfFontFamily, PdfStandardFont } from '@syncfusion/ej2-pdf-export';
import { sortOnProperty } from 'app/app.component';
import { ActivatedRoute } from '@angular/router';
import { SharedModule } from 'app/core/shared.module';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatRadioModule } from '@angular/material/radio';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import {
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';

@Component({
    selector: 'app-public-gantt',
    templateUrl: './public-gantt.component.html',
    imports: [
        SharedModule,
        MatSidenavModule,
        MatButtonModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        MatOptionModule,
        MatSlideToggleModule,
        MatRadioModule,
        TextFieldModule,
        MatTooltipModule,
        MatFormFieldModule,
        FormsModule,
        ReactiveFormsModule,
        GanttAllModule,
    ],
    styles: [
        `
            .mat-mdc-unelevated-button:not(:disabled) {
                background-color: var(--fuse-primary);
                color: white;
            }
        `,
    ],
    styleUrls: ['./../gantt/project-gantt.component.scss'],
})
export class PublicGanttComponent extends ProjectHelper implements OnInit {
    @ViewChild('projectGantt') projectGantt: GanttComponent;
    costManager: CostManagerDto;

    public ganttForm: FormGroup;
    public isCorrectPassword: boolean = false;
    public gantt: PublicGanttChartDto;

    private dataSource: any[];
    public data: any[];
    public eventMarkers?: object[];

    public columns: object[];

    public taskSettings: object;
    public labelSettings: object;
    public splitterSettings: object;
    public editSettings: EditSettingsModel;
    public selectionSettings: any; //SelectionSettingsModel;

    public toolbarItems: any[];

    public projectStartDate: Date;
    public projectEndDate: Date;

    constructor(
        private projectService: ProjectService,
        private cdr: ChangeDetectorRef,
        private datePipe: DatePipe,
        private route: ActivatedRoute,
        private _formBuilder: FormBuilder,
    ) {
        super();
    }

    public ngOnInit(): void {
        this.hideTrial();
        this.SetupGantt();

        this.route.params.subscribe((res) => {
            var publicId = res['id'];

            if (!publicId) return;

            this.buildForm();

            this.LoadGanttChart(publicId);
        });
    }

    buildForm() {
        this.ganttForm = this._formBuilder.group({
            password: ['', Validators.required],
        });

        this.cdr.detectChanges();
    }

    private hideTrial() {
        //hack away the syncfusion popup while we figure out how to make it recognise the license key
        var spans = document.getElementsByTagName('span');
        var searchText =
            'This application was built using a trial version of Syncfusion';

        for (var i = 0; i < spans.length; i++) {
            if (spans[i].textContent.includes(searchText)) {
                spans[i].parentElement.remove();
                break;
            }
        }
    }

    private SetupGantt() {
        this.toolbarItems = [];

        this.taskSettings = {
            id: 'taskId',
            name: 'taskName',
            startDate: 'startDate',
            endDate: 'endDate',
            duration: 'duration',
            progress: 'progress',
            // dependency: 'predecessor', //no!
            child: 'children',
            resourceInfo: 'resources',
        };

        this.columns = [
            {
                field: 'taskName',
                headerText: 'Task Name',
                /*width: '200',*/
                clipMode: 'EllipsisWithTooltip',
                edit: {
                    create: () => {
                        console.log('column - adding task');
                    },
                    read: () => {
                        console.log('column - reading task');
                    },
                    destroy: () => {
                        console.log('column - deleting task');
                    },
                    write: (args: Object) => {
                        console.log('column - editing task');
                    },
                },
            },
            {
                field: 'taskId',
                visible: false,
                isPrimaryKey: true,
                allowEditing: false,
            },
        ];

        this.labelSettings = {
            taskLabel: 'taskName',
        };

        this.splitterSettings = {
            position: '15%',
        };

        this.editSettings = {
            allowAdding: false,
            allowEditing: false,
            allowDeleting: false,
            allowTaskbarEditing: false,
            mode: 'Auto',
        };

        this.selectionSettings = {
            mode: 'Cell',
            checkboxOnly: true,
        };
    }

    private getEventModelFromTask = (task: WorkTaskListDto, i: number) =>
        <EventSettingsModel>{
            taskId:
                (this.dataSource?.length
                    ? Math.max(...this.dataSource.map((x) => x.taskId))
                    : 0) +
                i +
                1, //task.isResourceTask ? `r_${task.workTaskId}` : `u_${task.workTaskId}`,
            taskName: task.name,
            startDate: new Date(task.scheduledFrom),
            endDate: new Date(task.scheduledTo),
            duration:
                TimeUtil.getDaysBetween(
                    new Date(task.scheduledFrom),
                    new Date(task.scheduledTo),
                ) + 1, // task.hoursEstimate,
            progress:
                task.hoursEstimate == 0
                    ? 0
                    : (100 * task.hoursSpent) / task.hoursEstimate,
            // resourceNames: this.getUserOrResourceName(task),

            // resourceUniqueId: task.isResourceTask ? `r_${task.userId}` : `u_${task.userId}`,
            // resources: [(<any>this.resources.find((x: any) => x.uniqueId == (task.isResourceTask ? `r_${task.userId}` : `u_${task.userId}`)))?.resourceId ?? 0],
            // resources: [this.getUserOrResourceName(task)],
            children: [],
            showOnPdf: true,

            isClientTask: false,
            isHeader: false,
            colourHex: this.getColour(task.ganttColourId),
            ganttChartId: task.ganttChartId,
            ganttColourId: task.ganttColourId,
            icon: task.ganttIcon,
            position: task.ganttPosition,

            //task dto properties
            isResourceTask: task.isResourceTask,
            workTaskId: task.workTaskId,

            client: task.client,
            hoursEstimate: task.hoursEstimate,
            hoursSpent: task.hoursSpent,
            description: task.description,
            isBillable: task.isBillable,
            isPending: task.isPending,
            isFromProducerBooked: task.isFromProducerBooked,
            isLiveProject: task.isLiveProject,
            isCompleted: task.isCompleted,
            overrideRate: task.overrideRate,
            hideWeekends: task.hideWeekends,
            name: task.name,
            projectId: task.projectId,
            projectName: task.projectName,
            scheduledFrom: task.scheduledFrom,
            scheduledTo: task.scheduledTo,
            userId: task.userId,
            creatorUserId: task.creatorUserId,
            projectColour: task.projectColour,
            costSectionId: task.costSectionId,
            costSectionName: task.costSectionName,
            staffRateId: task.staffRateId,
        };

    private getEventModelFromClientTask = (task: GanttClientTaskDto) =>
        <EventSettingsModel>{
            taskId: task.ganttClientTaskId,
            taskName: task.name,
            startDate: task.isHeader ? null : new Date(task.scheduledFrom),
            endDate: task.isHeader ? null : new Date(task.scheduledTo),
            duration:
                TimeUtil.getDaysBetween(
                    new Date(task.scheduledFrom),
                    new Date(task.scheduledTo),
                ) + 1, // task.hoursEstimate,
            // progress: task.hoursEstimate == 0 ? 0 : 100 * task.hoursSpent / task.hoursEstimate,
            // resourceNames: this.getUserOrResourceName(task),

            // resources: [(<any>this.resources.find((x: any) => x.uniqueId == (task.isResourceTask ? `r_${task.userId}` : `u_${task.userId}`)))?.resourceId ?? 0],

            isClientTask: true,
            isHeader: task.isHeader,

            children: [],
            showOnPdf: task.showOnPdf ?? true,
            position: task.position,

            //task dto properties
            ganttClientTaskId: task.ganttClientTaskId,
            ganttChartId: task.ganttChartId,
            description: task.description,
            name: task.name,
            scheduledFrom: task.scheduledFrom,
            scheduledTo: task.scheduledTo,
            colourId: task.colourId,
            colourHex: this.getColour(task.colourId),
            icon: task.icon,
        };

    LoadGanttChart(ganttId: string) {
        this.projectService
            .apiProjectPublicGanttGet$Json$Response({ ganttId: ganttId })
            .subscribe((res) => {
                if (!res.body.data) return;

                this.gantt = res.body.data;
                console.log('gantt', this.gantt);

                this.projectStartDate = new Date(this.gantt.projectStartDate);
                this.projectEndDate = new Date(this.gantt.projectEndDate);

                if (this.projectGantt) this.PutGanttEventsToDataSource();

                // var workloadMilestones = this.gantt.milestones.filter(x => x.projectId == this.gantt?.projectId);
                // this.eventMarkers = workloadMilestones.map(x => <any>{
                //     day: new Date(x.date),
                //     label: x.name,
                // });

                this.cdr.detectChanges();
            });
    }

    PutGanttEventsToDataSource() {
        //set up tasks
        console.log('client tasks', this.gantt?.clientTasks);
        console.log('work tasks', this.gantt?.workTasks);

        var clientTasks = this.gantt?.clientTasks.map((x, i) =>
            this.getEventModelFromClientTask(x),
        );

        var workTasks = this.gantt?.workTasks
            ?.filter((x) => x.ganttChartId == this.gantt.ganttChartId)
            .map((x, i) => this.getEventModelFromTask(x, i));

        var sortedData = sortOnProperty(
            [...(clientTasks ?? []), ...(workTasks ?? [])],
            'position',
        );

        console.log('sorted data', sortedData);

        //figure out headers/children
        var headerIndexes = sortedData
            .map((x, i) => (x['isHeader'] ? i : null))
            .filter((x) => x != null);

        if (headerIndexes.length) {
            var lastIndex = sortedData.length;

            for (var i = 0; i < headerIndexes.length; i++) {
                var headerI = headerIndexes[i];
                var groupEndI = [...headerIndexes, lastIndex][i + 1];
                var header = sortedData[headerI];

                var children = sortedData.filter(
                    (_, i) => i > headerI && i < groupEndI,
                );

                header['children'] = children;
            }
            sortedData = sortedData.filter(
                (x, i) => i < headerIndexes[0] || x['isHeader'],
            );
        }

        this.dataSource = sortedData;
        this.data = [...this.dataSource];
        console.log('data', this.data);

        this.projectGantt.dataSource = this.data;
        this.projectGantt.refresh();

        this.cdr.detectChanges();
    }

    public tryLogin() {
        console.log('try login', this.ganttForm.value);
        var password = this.ganttForm.value.password;
        this.projectService
            .apiProjectCheckGanttPasswordGet$Json$Response({
                ganttId: this.gantt?.ganttChartId,
                password: password,
            })
            .subscribe((res) => {
                if (!res?.body?.data) return;

                this.isCorrectPassword = res.body.data;
                this.PutGanttEventsToDataSource();
            });
    }

    public toolbarClickHandler(args: any) {
        console.log('toolbar click', args.item);

        switch (args.item.id) {
            case 'projectGantt_pdfexport':
                this.exportPdf();
                break;
        }
    }

    exportPdf() {
        console.log('trying to export pdf');

        //set up view for export
        var pdfData: any[] = JSON.parse(
            JSON.stringify(this.data.filter((x) => x.showOnPdf)),
        );
        for (var d of pdfData)
            d.children = d.children?.filter((x) => x.showOnPdf);

        console.log('pdf data', pdfData);

        //remove emojis from any text
        //text.replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g, '')

        this.columns.find((x) => x['field'] == 'showOnPdf')['visible'] = false;

        var previousRightLabel = this.labelSettings['rightLabel'];
        this.labelSettings['rightLabel'] = '';
        this.projectGantt.labelSettings = Object.assign({}, this.labelSettings);

        this.projectGantt.dataSource = [...pdfData];
        this.projectGantt.refresh();

        let exportProperties: PdfExportProperties = {
            fitToWidthSettings: {
                isFitToWidth: true,
            },
            fileName:
                this.gantt?.projectName.trim().replace(' ', '') + '-gantt.pdf',
            header: {
                fromTop: 0,
                height: 65, // 72 pts = 1 inch
                contents: [
                    // {
                    //     type: 'Image',
                    // //   src: 'http://localhost:4200/assets/images/logo/hc_full.jpeg', // OR base64 image string
                    // // src: 'https://www.hellocharliehub.com/assets/images/logo/hc_full.jpeg', //SWITCH TO THIS FOR PROD DEPLOY
                    //     // src: 'data:image/jpeg;base64,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',
                    //     src: 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD/2wBDAAICAgICAgMCAgIDAwMDBAYEBAQEBAgGBQUFBQYICQcICQoKCQwNDAoMDAwMDAwMDAwMDAwMDAwMDAwMDAz/2wBDAQMDAwQDBAgEBAgMEAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAz/wAARCAAIAAgDAREAAhEBAxEB/8QAFgABAQEAAAAAAAAAAAAAAAAABAcI/8QAIRAAAgICAgMBAAAAAAAAAAAAAQIDBBExAAUSI0FhgdH/xAAVAQEBAAAAAAAAAAAAAAAAAAACA//EABsRAAEEAwAAAAAAAAAAAAAAAAECAwQREiEy/9oADAMBAAIRAxEAPwDNbXvJpt2+U2rWSTFuAEGmTgxu4/DlqXzFLRVIhZ5rLK1XimycSJE0ZFlJYcq5yTnUjz88RaiV1b/2Q==',
                    //     position: { x: 10, y: 10 }, // Top-left corner of the image inside header
                    //     size: { height: 50, width: 60 } // Customize image size
                    // },
                    // {
                    //     type: 'Image',
                    //     src: 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD/2wBDABALDA4MChAODQ4SERATGBYUGBgaGBYYHBwcHBweHB0dHR4hISIjIiQlJigpKCkqKCo2NjsuMDEwLzM0NDQyP/2wBDAQ8ODxAUGBQUFBAeHBweHiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiIiP/wAARCAAZABIDASIAAhEBAxEB/8QAFgABAQEAAAAAAAAAAAAAAAAAAAEF/8QAIBAAAQQCAQUAAAAAAAAAAAAAAQACAwQFEQYhEhTiQf/EABQBAQAAAAAAAAAAAAAAAAAAAAP/xAAYEQADAQEAAAAAAAAAAAAAAAAAAgMhEf/aAAwDAQACEQMRAD8AtPRG7yUo2u3HNHsJqWvXsojl4OpEbnz7oY2pZP0PZYw58K8DYr19M1Ar3GVohHmtuK9f/Z',
                    //     position: { x: 150, y: 10 },
                    //     size: { height: 50, width: 150 }
                    // },
                    {
                        type: 'Text',
                        value: `Project Plan - ${this.gantt?.projectName.trim()} - ${this.datePipe.transform(new Date(), 'd MMM yyyy')}`,
                        position: { x: 120, y: 10 }, // Where the text will appear
                        font: new PdfStandardFont(PdfFontFamily.Helvetica, 20),
                        style: {
                            textBrushColor: '#000000',
                            fontSize: 14,
                        },
                    },
                ],
            },
        };

        setTimeout(() => {
            this.projectGantt.pdfExport(exportProperties).then((res) => {
                console.log('exported?');

                //put the gantt view back
                this.columns.find((x) => x['field'] == 'showOnPdf')['visible'] =
                    true;
                this.labelSettings['rightLabel'] = previousRightLabel;
                this.projectGantt.labelSettings = Object.assign(
                    {},
                    this.labelSettings,
                );
                this.data = [...this.dataSource];
                this.projectGantt.dataSource = [...this.data];
                this.projectGantt.refresh();
            });
        }, 100); // 100ms is usually enough; tweak if needed
    }

    public async actionBegin(args: any) {
        console.log('action begin', args);
        args.cancel = true;
    }

    public actionComplete(args: any): void {}

    getColour(colourId: number) {
        if (!this.gantt) return;

        return (
            this.gantt.colours.find((x) => x.ganttColourId == colourId)
                ?.colour ?? '#607d8b'
        ); // grey
    }

    queryTaskbar(args) {
        args.taskbarBgColor = args.data.colourHex;

        const el = args.taskbarElement as HTMLElement;
        el.style.backgroundColor = args.data.colourHex;
        el.style.borderColor = args.data.colourHex;

        //if task has icon
        if (args.data.icon) {
            var label = el.getElementsByClassName(
                'e-task-label',
            )[0] as HTMLElement;
            label.style.display = 'flex';
            label.style.alignItems = 'center';
            label.insertAdjacentHTML(
                'afterbegin',
                `<span class="material-icons mr-1" style="font-size: 18px">${args.data.icon}</span>`,
            );
        }

        //if task is a header
        if (args.data.isHeader) args.taskbarElement.style.display = 'none';
    }

    queryCellEvent(args) {
        if (args.data.isHeader) args.cell.classList.add('gantt-header-row');
    }

    onDataBound(): void {
        console.log('on data bound');

        setTimeout(() => {
            this.displayWeekends();
        }, 50);
    }

    displayWeekends() {
        //clear all weekend blocks
        var blocks = document.getElementsByClassName('e-weekend');

        try {
            for (var i = 0; i < blocks.length; i++)
                document.removeChild(blocks[i]);
        } catch (e) {}

        var container = document.getElementsByClassName(
            'e-chart-rows-container',
        )[0];

        const projectStart = TimeUtil.setTo1amUTC(
            new Date(this.projectStartDate),
        );
        const projectEnd = TimeUtil.setTo1amUTC(new Date(this.projectEndDate));
        const cellWidth = 33;

        var date = new Date(projectStart);

        while (date <= projectEnd) {
            if (date.getDay() == 0 || date.getDay() == 6) {
                //add vertical strip
                const daysFromStart = Math.ceil(
                    (date.getTime() - projectStart.getTime()) /
                        (1000 * 60 * 60 * 24),
                );

                const overlay = document.createElement('div');
                overlay.classList.add('e-weekend');
                overlay.style.height = '100%';
                overlay.style.width = `${cellWidth}px`;
                overlay.style.pointerEvents = 'none';
                overlay.style.left = `${daysFromStart * cellWidth - 2.75}px`;

                container.appendChild(overlay);
            }

            date = TimeUtil.addDays(date, 1);
        }
    }
}
